/**
 * The catalogue row and domain colour behind one indicator id — what a *header* needs before
 * the numbers land.
 *
 * A module of its own so `IndicatorAnalytics.tsx` exports components only (react-refresh),
 * and because three surfaces now ask the same question: §F5.9's popover (to title and colour
 * its `Widget`), R7's sidebar section (to name what is mapped), and the analytics body
 * itself. One hook, one pair of query keys, one cache entry.
 */
import { useQuery } from '@tanstack/react-query';

import { fetchConfig, fetchIndicators, queryKeys, type Domain, type Indicator } from '../../lib/api';

/** Fill for an indicator whose domain is not in `/meta/config` yet. */
export const FALLBACK_DOMAIN_COLOR = '#2E7D32';

export interface IndicatorMeta {
  ind: Indicator | null;
  domain: Domain | null;
  color: string;
}

export function useIndicatorMeta(indicatorId: string | null, enabled = true): IndicatorMeta {
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
    enabled: enabled && Boolean(indicatorId),
  });
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const ind = indicators?.find((i) => i.id === indicatorId) ?? null;
  const domain = config?.domains.find((d) => d.id === ind?.domain) ?? null;
  return { ind, domain, color: domain?.color ?? FALLBACK_DOMAIN_COLOR };
}
